/**
 * Chat model - explicit state definition.
 * 
 * Elm-ish architecture: State is explicit and typed.
 * No implicit state machines encoded in useState hooks.
 * 
 * Scope: server_readable channels only.
 * client_sealed (E2EE) is intentionally not modeled.
 */

/**
 * Chat channel for display.
 *
 * CORRECTION01 (R2 fix): the channel display now carries the S014
 * counterpart projection. The ChatHeader reads counterpart directly
 * from the model so the header renders name/age/city on the reload
 * path (GET /api/v1/chat/channels/{id}) and on the list path.
 */
export interface ChatChannelDisplay {
  readonly id: string;
  readonly createdAt: Date;
  readonly messageCount: number;
  readonly counterpart?: ChatCounterpartDisplay;
}

/**
 * Display-shape counterpart, derived from the API ChatCounterpartSummary
 * via ChatHeader's local ChatCounterpartSummary type. The display
 * shape is canonical for the chat view layer.
 */
export interface ChatCounterpartDisplay {
  readonly profileId: string;
  readonly displayName: string;
  readonly age?: number;
  readonly cityOrRegion?: string;
  readonly photoUrl?: string | null;
}

/**
 * Chat message for display.
 */
export interface ChatMessageDisplay {
  readonly id: string;
  readonly content: string;
  readonly senderAccountId: string;
  readonly createdAt: Date;
}

/**
 * View mode for the chat page.
 */
export type ChatViewMode =
  | 'channel-list'   // Showing list of channels
  | 'channel-detail' // Showing messages in a channel
  | 'creating';      // Creating a new channel

/**
 * Status values for the chat program lifecycle.
 */
export type ChatStatus =
  | 'idle'           // Initial state
  | 'loading'        // Loading data
  | 'loaded'         // Data loaded successfully
  | 'error';         // An error occurred

/**
 * Realtime connection status. Surfaced by the chat program so the
 * view can render a "live" / "reconnecting" badge.
 */
export type RealtimeStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

/**
 * Complete state shape for the Chat program.
 *
 * Security posture:
 * - All state is local to this program instance
 * - No encryption keys stored or exposed
 * - Error messages are user-safe (no internal details)
 * - client_sealed mode is not represented (not exposed via API)
 */
export interface ChatModel {
  /** Current view mode */
  readonly viewMode: ChatViewMode;

  /** Program status */
  readonly status: ChatStatus;

  /** List of channels */
  readonly channels: readonly ChatChannelDisplay[];

  /** Currently selected channel ID */
  readonly selectedChannelId: string | null;

  /**
   * CORRECTION01 (R2 fix): the selected channel metadata (including
   * the S014 counterpart projection). The ChatHeader reads this so
   * it renders name/age/city on the reload path AND on the navigation
   * path. Null while the channel is loading or the load failed.
   */
  readonly selectedChannel: ChatChannelDisplay | null;

  /** Messages in the selected channel */
  readonly messages: readonly ChatMessageDisplay[];

  /** New message input text */
  readonly newMessageText: string;

  /** Error message when status is 'error' */
  readonly error: string | null;

  /** Whether we have more channels to load */
  readonly hasMoreChannels: boolean;

  /** Whether we have more messages to load */
  readonly hasMoreMessages: boolean;

  /** Whether the "create channel" modal/form is open */
  readonly showCreateForm: boolean;

  /** Whether we're currently creating a channel */
  readonly isCreating: boolean;

  /** Realtime connection status (ACT-INDEEP-SPONSOR-FRIEND-PERSONAL-CHAT-REALTIME01) */
  readonly realtimeStatus: RealtimeStatus;

  /**
   * Set of message ids that have already been folded into the
   * model. The pure update reducer uses this to dedup live events
   * against the synthetic state added by HTTP sends.
   */
  readonly seenMessageIds: ReadonlySet<string>;
}

/**
 * Initial/empty state for the chat program.
 */
export const initialModel: ChatModel = {
  viewMode: 'channel-list',
  status: 'idle',
  channels: [],
  selectedChannelId: null,
  // CORRECTION01 (R2 fix): selected channel is null until the
  // channel-detail load completes. The ChatHeader reads this field.
  selectedChannel: null,
  messages: [],
  newMessageText: '',
  error: null,
  hasMoreChannels: false,
  hasMoreMessages: false,
  showCreateForm: false,
  isCreating: false,
  realtimeStatus: 'disconnected',
  seenMessageIds: new Set<string>(),
};

/**
 * Convert API channel to display format.
 *
 * CORRECTION01 (R2 fix): counterpart is propagated into the display
 * form so the ChatHeader can render name/age/city.
 */
export function toChannelDisplay(
  id: string,
  createdAt: string,
  counterpart?: ChatCounterpartDisplay
): ChatChannelDisplay {
  return {
    id,
    createdAt: new Date(createdAt),
    messageCount: 0,
    counterpart,
  };
}

/**
 * Convert API message to display format.
 */
export function toMessageDisplay(
  id: string,
  content: string,
  senderAccountId: string,
  createdAt: string
): ChatMessageDisplay {
  return {
    id,
    content,
    senderAccountId,
    createdAt: new Date(createdAt),
  };
}

/**
 * Coerce a realtime message envelope into the display shape.
 */
export function fromRealtimeMessage(
  message: { id: string; content: string; senderAccountId: string; createdAt: string }
): ChatMessageDisplay {
  return {
    id: message.id,
    content: message.content,
    senderAccountId: message.senderAccountId,
    createdAt: new Date(message.createdAt),
  };
}

/**
 * Check if we can send a message.
 */
export function canSendMessage(model: ChatModel): boolean {
  return (
    model.viewMode === 'channel-detail' &&
    model.newMessageText.trim().length > 0 &&
    model.selectedChannelId !== null
  );
}

/**
 * Check if the program is in an error state.
 */
export function isErrorState(model: ChatModel): boolean {
  return model.status === 'error';
}

/**
 * Check if we're viewing a channel.
 */
export function isViewingChannel(model: ChatModel): boolean {
  return model.viewMode === 'channel-detail' && model.selectedChannelId !== null;
}
